import type { MapArtifact } from "@urlcade/contracts";
import { SIMULATION_VERSION } from "@urlcade/contracts";

export interface LocalScore {
  mapId: string;
  mapTitle: string;
  artifactHash: string;
  simulationVersion: string;
  elapsedMs: number;
  penaltyMs: number;
  completedAt: string;
  finished?: boolean;
  checkpoint?: number;
}

const key = "urlcade:local-scores:v1";
const maxEntries = 40;

export function readLocalScores(): LocalScore[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    if (!Array.isArray(raw)) return [];
    return raw.filter((score): score is LocalScore => {
      if (!score || typeof score !== "object") return false;
      const row = score as Record<string, unknown>;
      return typeof row.mapId === "string" && typeof row.mapTitle === "string" && typeof row.artifactHash === "string" &&
        row.simulationVersion === SIMULATION_VERSION && Number.isFinite(row.elapsedMs) && Number(row.elapsedMs) >= 0 &&
        Number.isFinite(row.penaltyMs) && Number(row.penaltyMs) >= 0 && typeof row.completedAt === "string" &&
        (row.finished === undefined || typeof row.finished === "boolean") &&
        (row.checkpoint === undefined || (Number.isInteger(row.checkpoint) && Number(row.checkpoint) >= 0 && Number(row.checkpoint) <= 5));
    });
  } catch {
    return [];
  }
}

export function saveLocalScore(map: MapArtifact, elapsedMs: number, penaltyMs: number, finished = true, checkpoint = 0): LocalScore | null {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0 || !Number.isFinite(penaltyMs) || penaltyMs < 0 || !Number.isInteger(checkpoint) || checkpoint < 0 || checkpoint > 5) return null;
  const score: LocalScore = {
    mapId: map.mapId,
    mapTitle: map.title,
    artifactHash: map.artifactHash,
    simulationVersion: SIMULATION_VERSION,
    elapsedMs,
    penaltyMs,
    completedAt: new Date().toISOString(),
    finished,
    checkpoint,
  };
  try {
    const rows = [score, ...readLocalScores()].slice(0, maxEntries);
    localStorage.setItem(key, JSON.stringify(rows));
    return score;
  } catch {
    return null;
  }
}

export function getBestLocalScore(map: Pick<MapArtifact, "artifactHash">): LocalScore | null {
  return readLocalScores()
    .filter((score) => score.artifactHash === map.artifactHash && score.simulationVersion === SIMULATION_VERSION && score.finished !== false)
    .sort((a, b) => a.elapsedMs + a.penaltyMs - (b.elapsedMs + b.penaltyMs))[0] ?? null;
}

export function clearLocalScores(): void {
  try { localStorage.removeItem(key); } catch { /* Storage may be disabled by the browser. */ }
}
